import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Spin, Input, Select } from 'antd';
import { documents as documentsActions } from '../../actions/database/documents';
import { customers as customersActions } from '../../actions/database/customers';
import { CUSTOMERS, UPLOAD, DOCUMENTS } from '../../../application/types';
import { logClient } from '../../lib/client_log';
import { memberEntry } from '../../lib/customer_links';
import { isScoped } from '../../lib/user_scope';
import { DocumentsContext } from './context';
import { ListFolders, useListFolder } from './list_folders';
import { ModalFolder, useModalFolder } from './modal_folder';
import { ModalTemplate, useModalTemplate } from './modal_template';
import { ModalSearchFile, useModalSearchFile } from './modal_search_file';
import { RestrictAccessDrawer } from './restrict_access';
import { File } from './file';

export const Documents = () => {
  const dispatch = useDispatch();
  const docs = useSelector(state => state.get('documents').get('read'));
  const auth = useSelector(state => state.get('authenticate').get('login'));
  const roles = useSelector(state => state.get('roles').get('roles').roles);
  const customers = useSelector(state => state.get('customers').get('read'));

  // Default a "Razon social" para que al abrir Documentos se muestre directamente
  // el selector de clientes por nombre (en lugar de exigir buscar por NIT).
  const [select, setSelect] = useState("Razon social");
  const [clientNames, setClientNames] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [selectedClient, setSelectedClient] = useState(null);

  const [form] = useModalFolder();
  const { templates } = useModalTemplate();
  const { search } = useModalSearchFile();
  const { folders } = useListFolder({ form });

  const loadCustomers = value => {
    folders.set({ ...folders.data, isLoad: false });
    folders.current.isUpload.set(false);
    folders.list.set({ loading: false, columns: [], source: [] });

    if (!value || value.trim().length <= 0) {
      if (select === "Razon social") {
        dispatch(
          customersActions.read({
            roles: roles.documents,
            filter: { field: select, value: "" }
          })
        ).then(({ customers }) => {
          const clientNames = customers.map(customer => ({
            name: customer.data['Razon social'],
            id: customer.id
          }));
          setClientNames(clientNames);
          console.log("Clientes encontrados:", clientNames.map(client => client.name));
          folders.set({ ...folders.data, isLoad: true });
        });
      } else {
        dispatch({ type: CUSTOMERS.READ.READ_CUSTOMERS });
      }
      return;
    }

    dispatch(
      customersActions.read({
        roles: roles.documents,
        filter: { field: select, value }
      })
    ).then(({ customers }) => {
      // OJO: aquí NO se actualiza clientNames. Esta consulta llega filtrada a
      // UN cliente (el seleccionado); si se usara para las opciones, el
      // dropdown quedaba con un solo cliente y no se podía buscar otro.
      // La lista completa de opciones se carga al montar (loadCustomers(""))
      // y se conserva durante toda la sesión.
      logClient({ event: 'dbg.documents.customers_found', level: 'info', message: 'customers.read OK', data: { count: customers.length, names: customers.map(c => c.data['Razon social']) } });
      Promise.all(
        customers.map(customer =>
          dispatch(documentsActions.read({ uid: customer.id, roles: roles.documents })).then(({ data, type }) => {
            logClient({ event: 'dbg.documents.read_then', level: 'info', message: 'documents.read .then ejecutado', data: { customerId: customer.id, customerName: customer.data['Razon social'], hasData: !!data, dataPath: data?.path, actionType: type } });
            const user = memberEntry(customer.data, auth.user.uid);
            // Admin de esta empresa: marcado en Usuarios[], o supervisora de
            // empresa (alcance 'empresas', spec 2026-10-05) que es miembro.
            const isCompanyAdmin = user?.isAdmin === true || (isScoped(roles) && !!user);

            folders.routes.set([{ name: customer.data['Razon social'], reference: data }]);
            folders.current.reference.set(data);
            folders.current.user.isRoot.set(roles?.documents?.level === 'all');
            folders.current.user.isAdmin.set(isCompanyAdmin);
            // Motor (spec 2026-10-06): root solo por documents.root; admin
            // solo por Usuarios[].isAdmin (la supervisora con alcance va por
            // su rol, lib/role_seed).
            folders.current.company.set({
              cid: customer.id,
              isRootUser: roles?.documents?.root === true,
              isAdmin: user?.isAdmin === true,
              linked: !!user,
            });
            folders.current.user.permissions.set({ visible: false, users: customer?.data['Usuarios'] || [] });
            folders.set({ ...folders.data, isLoad: true });
            logClient({ event: 'dbg.documents.set_isLoad', level: 'info', message: 'folders.set isLoad=true', data: { customerId: customer.id, isAdmin: isCompanyAdmin, isRoot: roles?.documents?.level === 'all' } });
          }).catch(err => {
            logClient({ event: 'dbg.documents.read_catch', level: 'error', message: 'documents.read .catch', data: { customerId: customer.id, code: err?.code, errMessage: err?.message, errType: err?.type } });
          })
        )
      ).catch((err) => {
        logClient({ event: 'dbg.documents.promise_all_catch', level: 'error', message: 'Promise.all catch', data: { errMessage: err?.message, code: err?.code } });
        folders.list.set({ loading: false, columns: [], source: [] });
      });
    });
  };

  useEffect(() => {
    dispatch({ type: CUSTOMERS.READ.READ_CUSTOMERS });
    dispatch({ type: UPLOAD.UPLOAD });
    dispatch({ type: DOCUMENTS.READ.READ_DOCUMENTS });

    if (select === "Razon social") {
      loadCustomers("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, select]);

  const handleClientSelect = value => {
    if (!value) {
      setSelectedClient(null);
      loadCustomers("");  // Reload customers to show the full list again
    } else {
      const selected = clientNames.find(client => client.name === value);
      if (selected) {
        setSelectedClient(selected.name);
        loadCustomers(selected.name);
      }
    }
  };

  return (
    <DocumentsContext.Provider value={{ folders, templates, search }}>
      <div style={{ marginBottom: 15, display: 'flex', alignItems: 'center' }}>
        <Select
          value={select}
          style={{ width: 120, marginRight: 10 }}
          onSelect={value => setSelect(value)}
        >
          <Select.Option value="NIT">Documentos</Select.Option>
          <Select.Option value="Razon social">Nombre</Select.Option>
        </Select>
        {select === "Razon social" ? (
          <Select
            showSearch
            style={{ flex: 1 }}
            placeholder="Seleccione un cliente"
            optionFilterProp="children"
            onChange={handleClientSelect}
            allowClear
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
           {clientNames
  .filter(client => client.name !== undefined)
  .map(client => (
    <Select.Option key={client.id} value={client.name}>
      {client.name}
    </Select.Option>
  ))
}
          </Select>
        ) : (
          <Input.Search
            enterButton="Buscar"
            style={{ flex: 1 }}
            onSearch={loadCustomers}
          />
        )}
      </div>

      {customers?.type === CUSTOMERS.READ.READED_CUSTOMERS
        ? customers?.customers?.length > 0
          ? <ListFolders /> : null
        : customers?.type === CUSTOMERS.READ.READING_CUSTOMERS
          ? <div style={{ textAlign: 'center' }}><Spin /></div>
          : null}

      {folders.current.user.roles?.folders?.create && !folders.current.isClosing.data
        ? <>
          <ModalFolder form={form} />
          <ModalTemplate />
        </>
        : null}

      {docs?.data && folders.current.isUpload.data ? <ModalSearchFile /> : null}

      <RestrictAccessDrawer />

      <File />
    </DocumentsContext.Provider>
  );
};
